import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ReplaySubject, Observable, of } from 'rxjs';
import { mergeMap, tap, map, switchMap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { State, toODataString } from '@progress/kendo-data-query';
import { DialogService, DialogRef, DialogCloseResult } from '@progress/kendo-angular-dialog';

import { TicketStatus } from './ticket-status.model';
import { UtilService } from 'src/app/shared/util.service';
import { LocalStorage } from 'src/app/core/local-storage.enum';

const apiUrl = environment.apiUrl;

@Injectable({
  providedIn: 'root',
})
export class TicketStatusService {
  private ticketstatuss: ReplaySubject<TicketStatus[]> = new ReplaySubject<TicketStatus[]>(1);
  public ticketstatus: ReplaySubject<TicketStatus> = new ReplaySubject<TicketStatus>(1);
  public ticketstatussOdata: ReplaySubject<any> = new ReplaySubject<any>(1);
  private ticketStatusList: TicketStatus[] = []; // liste des ticketstatuss dans la cache
  private dialog!: DialogRef;

  constructor(private http: HttpClient, private dialogService: DialogService, private utilService: UtilService) {}

  initOne() {
    this.ticketstatus.next(new TicketStatus());
  }

  list(): Observable<TicketStatus[]> {
    let uri = `${apiUrl}services/TicketStatus/GetListRaw`;
    return this.http.get<TicketStatus[]>(uri).pipe(
      mergeMap((response: any) => {
        let value = response.value;
        this.ticketstatuss.next(value);
        return of(value);
      })
    );
  }

  // CACHE

  /**
   * cette fonction retourne tous les records mis en cache dans le localStorage.
   * si la cache n'a pas été rafraichie depuis 20 secondes, on la rafraichi.
   * on fetch les données de l'API de facon recursive pour obtenir tous les records.
   * @param forceFetch
   * @returns la liste de tous les records
   */

  listCache(forceFetch?: boolean): Observable<TicketStatus[]> {
    let fetch = false;
    // read localStorage
    let ls = localStorage.getItem(LocalStorage.CACHE_TICKET_STATUS);
    if (forceFetch) {
      fetch = true;
    } else if (ls != null && ls.length > 0) {
      let data = JSON.parse(ls!);
      let now = Date.now();
      // force to refresh every X seconds
      if (now - data.lastUpdate > 20000) {
        fetch = true;
      }
    } else {
      // empty localstorage
      fetch = true;
    }
    // fetch or return cache data
    if (fetch) {
      // reset cache et start fetching
      localStorage.removeItem(LocalStorage.CACHE_TICKET_STATUS);
      return this.fetchOData(apiUrl + 'services/TicketStatus/GetListRaw?$select=ticketStatusId,servicentreTextualCode,nameFr');
    } else {
      let data = JSON.parse(ls!);
      // update service variable
      this.ticketStatusList = data.data;
      return of(data.data);
    }
  }

  /*
   * cette fonction fetch les données de l'API de facon recursive pour obtenir tous les records.
   */
  private fetchOData(url: string): Observable<TicketStatus[]> {
    let uri = `${url}`;
    return this.http.get<any>(uri).pipe(
      switchMap((response) => {
        const data = response as TicketStatus[];
        // fetch actual cache & update it
        let existingCache = JSON.parse(localStorage.getItem(LocalStorage.CACHE_TICKET_STATUS)!) || { lastUpdate: 0, data: [] };
        existingCache.data = existingCache.data.concat(data);
        existingCache.lastUpdate = Date.now();
        localStorage.setItem(LocalStorage.CACHE_TICKET_STATUS, JSON.stringify(existingCache));
        // update service variable
        this.ticketStatusList = existingCache.data;
        // fetch next page if needed OR return data
        if (response['@odata.nextLink']) {
          // ATTENTION!!! ce endpoint n'est pas oDATA alors cette condition ne s'appliquera jamais
          return this.fetchOData(response['@odata.nextLink']).pipe(map((nextData) => data.concat(nextData)));
        } else {
          return of(data);
        }
      }),
      catchError((error) => {
        console.error('Error fetching data:', error);
        return of([]);
      })
    );
  }

  // ODATA : STATE

  odata(state: State): Observable<any> {
    let _state = this.utilService.makeStateCaseInsensitive(state);
    let uri = `${apiUrl}services/TicketStatus?${toODataString(_state)}&$count=true`;

    return this.http.get<TicketStatus>(uri).pipe(
      tap((response: any) => {
        this.ticketstatussOdata.next(response);
      })
    );
  }

  // ODATA  : STRING

  odataStr(state: State, oDataString: string): Observable<any> {
    let oDataFullString = this.utilService.getODataFullString(state, oDataString);
    let uri = `${apiUrl}services/TicketStatus?${oDataFullString}&$count=true`;
    return this.http.get<any>(uri);
  }
}
